import MathTool from '../math/MathTool.js';
import EasingTool from '../math/EasingTool.js';
//import earcut from "earcut";


function createEarcut() {
    function earcut(data, holeIndices, dim) {
      dim = dim || 2;
  
      var hasHoles = holeIndices && holeIndices.length,
        outerLen = hasHoles ? holeIndices[0] * dim : data.length,
        outerNode = linkedList(data, 0, outerLen, dim, true),
        triangles = [];
  
      if (!outerNode) return triangles;
  
      var minX, minY, maxX, maxY, x, y, invSize;
  
      if (hasHoles) outerNode = eliminateHoles(data, holeIndices, outerNode, dim);
  
      // if the shape is not too simple, we'll use z-order curve hash later; calculate polygon bbox
      if (data.length > 80 * dim) {
        minX = maxX = data[0];
        minY = maxY = data[1];
  
        for (var i = dim; i < outerLen; i += dim) {
          x = data[i];
          y = data[i + 1];
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
  
        // minX, minY and invSize are later used to transform coords into integers for z-order calculation
        invSize = Math.max(maxX - minX, maxY - minY);
        invSize = invSize !== 0 ? 1 / invSize : 0;
      }
  
      earcutLinked(outerNode, triangles, dim, minX, minY, invSize);
  
      return triangles;
    }
  
    // create a circular doubly linked list from polygon points in the specified winding order
    function linkedList(data, start, end, dim, clockwise) {
      var i, last;
  
      if (clockwise === signedArea(data, start, end, dim) > 0) {
        for (i = start; i < end; i += dim)
          last = insertNode(i, data[i], data[i + 1], last);
      } else {
        for (i = end - dim; i >= start; i -= dim)
          last = insertNode(i, data[i], data[i + 1], last);
      }
  
      if (last && equals(last, last.next)) {
        removeNode(last);
        last = last.next;
      }
  
      return last;
    }
  
    // eliminate colinear or duplicate points
    function filterPoints(start, end) {
      if (!start) return start;
      if (!end) end = start;
  
      var p = start,
        again;
      do {
        again = false;
  
        if (!p.steiner && (equals(p, p.next) || area(p.prev, p, p.next) === 0)) {
          removeNode(p);
          p = end = p.prev;
          if (p === p.next) break;
          again = true;
        } else {
          p = p.next;
        }
      } while (again || p !== end);
  
      return end;
    }
  
    // main ear slicing loop which triangulates a polygon (given as a linked list)
    function earcutLinked(ear, triangles, dim, minX, minY, invSize, pass) {
      if (!ear) return;
  
      // interlink polygon nodes in z-order
      if (!pass && invSize) indexCurve(ear, minX, minY, invSize);
  
      var stop = ear,
        prev,
        next;
  
      // iterate through ears, slicing them one by one
      while (ear.prev !== ear.next) {
        prev = ear.prev;
        next = ear.next;
  
        if (invSize ? isEarHashed(ear, minX, minY, invSize) : isEar(ear)) {
          // cut off the triangle
          triangles.push(prev.i / dim);
          triangles.push(ear.i / dim);
          triangles.push(next.i / dim);
  
          removeNode(ear);
  
          // skipping the next vertice leads to less sliver triangles
          ear = next.next;
          stop = next.next;
  
          continue;
        }
  
        ear = next;
  
        // if we looped through the whole remaining polygon and can't find any more ears
        if (ear === stop) {
          // try filtering points and slicing again
          if (!pass) {
            earcutLinked(
              filterPoints(ear),
              triangles,
              dim,
              minX,
              minY,
              invSize,
              1
            );
  
            // if this didn't work, try curing all small self-intersections locally
          } else if (pass === 1) {
            ear = cureLocalIntersections(ear, triangles, dim);
            earcutLinked(ear, triangles, dim, minX, minY, invSize, 2);
  
            // as a last resort, try splitting the remaining polygon into two
          } else if (pass === 2) {
            splitEarcut(ear, triangles, dim, minX, minY, invSize);
          }
  
          break;
        }
      }
    }
  
    // check whether a polygon node forms a valid ear with adjacent nodes
    function isEar(ear) {
      var a = ear.prev,
        b = ear,
        c = ear.next;
  
      if (area(a, b, c) >= 0) return false; // reflex, can't be an ear
  
      // now make sure we don't have other points inside the potential ear
      var p = ear.next.next;
  
      while (p !== ear.prev) {
        if (
          pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
          area(p.prev, p, p.next) >= 0
        )
          return false;
        p = p.next;
      }
  
      return true;
    }
  
    function isEarHashed(ear, minX, minY, invSize) {
      var a = ear.prev,
        b = ear,
        c = ear.next;
  
      if (area(a, b, c) >= 0) return false; // reflex, can't be an ear
  
      // triangle bbox; min & max are calculated like this for speed
      var minTX = a.x < b.x ? (a.x < c.x ? a.x : c.x) : b.x < c.x ? b.x : c.x,
        minTY = a.y < b.y ? (a.y < c.y ? a.y : c.y) : b.y < c.y ? b.y : c.y,
        maxTX = a.x > b.x ? (a.x > c.x ? a.x : c.x) : b.x > c.x ? b.x : c.x,
        maxTY = a.y > b.y ? (a.y > c.y ? a.y : c.y) : b.y > c.y ? b.y : c.y;
  
      // z-order range for the current triangle bbox;
      var minZ = zOrder(minTX, minTY, minX, minY, invSize),
        maxZ = zOrder(maxTX, maxTY, minX, minY, invSize);
  
      var p = ear.prevZ,
        n = ear.nextZ;
  
      // look for points inside the triangle in both directions
      while (p && p.z >= minZ && n && n.z <= maxZ) {
        if (
          p !== ear.prev &&
          p !== ear.next &&
          pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
          area(p.prev, p, p.next) >= 0
        )
          return false;
        p = p.prevZ;
  
        if (
          n !== ear.prev &&
          n !== ear.next &&
          pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, n.x, n.y) &&
          area(n.prev, n, n.next) >= 0
        )
          return false;
        n = n.nextZ;
      }
  
      // look for remaining points in decreasing z-order
      while (p && p.z >= minZ) {
        if (
          p !== ear.prev &&
          p !== ear.next &&
          pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
          area(p.prev, p, p.next) >= 0
        )
          return false;
        p = p.prevZ;
      }
  
      // look for remaining points in increasing z-order
      while (n && n.z <= maxZ) {
        if (
          n !== ear.prev &&
          n !== ear.next &&
          pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, n.x, n.y) &&
          area(n.prev, n, n.next) >= 0
        )
          return false;
        n = n.nextZ;
      }
  
      return true;
    }
  
    // go through all polygon nodes and cure small local self-intersections
    function cureLocalIntersections(start, triangles, dim) {
      var p = start;
      do {
        var a = p.prev,
          b = p.next.next;
  
        if (
          !equals(a, b) &&
          intersects(a, p, p.next, b) &&
          locallyInside(a, b) &&
          locallyInside(b, a)
        ) {
          triangles.push(a.i / dim);
          triangles.push(p.i / dim);
          triangles.push(b.i / dim);
  
          // remove two nodes involved
          removeNode(p);
          removeNode(p.next);
  
          p = start = b;
        }
        p = p.next;
      } while (p !== start);
  
      return p;
    }
  
    // try splitting polygon into two and triangulate them independently
    function splitEarcut(start, triangles, dim, minX, minY, invSize) {
      // look for a valid diagonal that divides the polygon into two
      var a = start;
      do {
        var b = a.next.next;
        while (b !== a.prev) {
          if (a.i !== b.i && isValidDiagonal(a, b)) {
            // split the polygon in two by the diagonal
            var c = splitPolygon(a, b);
  
            // filter colinear points around the cuts
            a = filterPoints(a, a.next);
            c = filterPoints(c, c.next);
  
            // run earcut on each half
            earcutLinked(a, triangles, dim, minX, minY, invSize);
            earcutLinked(c, triangles, dim, minX, minY, invSize);
            return;
          }
          b = b.next;
        }
        a = a.next;
      } while (a !== start);
    }
  
    // link every hole into the outer loop, producing a single-ring polygon without holes
    function eliminateHoles(data, holeIndices, outerNode, dim) {
      var queue = [],
        i,
        len,
        start,
        end,
        list;
  
      for (i = 0, len = holeIndices.length; i < len; i++) {
        start = holeIndices[i] * dim;
        end = i < len - 1 ? holeIndices[i + 1] * dim : data.length;
        list = linkedList(data, start, end, dim, false);
        if (list === list.next) list.steiner = true;
        queue.push(getLeftmost(list));
      }
  
      queue.sort(compareX);
  
      // process holes from left to right
      for (i = 0; i < queue.length; i++) {
        eliminateHole(queue[i], outerNode);
        outerNode = filterPoints(outerNode, outerNode.next);
      }
  
      return outerNode;
    }
  
    function compareX(a, b) {
      return a.x - b.x;
    }
  
    // find a bridge between vertices that connects hole with an outer ring and and link it
    function eliminateHole(hole, outerNode) {
      outerNode = findHoleBridge(hole, outerNode);
      if (outerNode) {
        var b = splitPolygon(outerNode, hole);
        filterPoints(b, b.next);
      }
    }
  
    // David Eberly's algorithm for finding a bridge between hole and outer polygon
    function findHoleBridge(hole, outerNode) {
      var p = outerNode,
        hx = hole.x,
        hy = hole.y,
        qx = -Infinity,
        m;
  
      // find a segment intersected by a ray from the hole's leftmost point to the left;
      // segment's endpoint with lesser x will be potential connection point
      do {
        if (hy <= p.y && hy >= p.next.y && p.next.y !== p.y) {
          var x = p.x + (hy - p.y) * (p.next.x - p.x) / (p.next.y - p.y);
          if (x <= hx && x > qx) {
            qx = x;
            if (x === hx) {
              if (hy === p.y) return p;
              if (hy === p.next.y) return p.next;
            }
            m = p.x < p.next.x ? p : p.next;
          }
        }
        p = p.next;
      } while (p !== outerNode);
  
      if (!m) return null;
  
      if (hx === qx) return m.prev; // hole touches outer segment; pick lower endpoint
  
      // look for points inside the triangle of hole point, segment intersection and endpoint;
      // if there are no points found, we have a valid connection;
      // otherwise choose the point of the minimum angle with the ray as connection point
  
      var stop = m,
        mx = m.x,
        my = m.y,
        tanMin = Infinity,
        tan;
  
      p = m.next;
  
      while (p !== stop) {
        if (
          hx >= p.x &&
          p.x >= mx &&
          hx !== p.x &&
          pointInTriangle(
            hy < my ? hx : qx,
            hy,
            mx,
            my,
            hy < my ? qx : hx,
            hy,
            p.x,
            p.y
          )
        ) {
          tan = Math.abs(hy - p.y) / (hx - p.x); // tangential
  
          if (
            (tan < tanMin || (tan === tanMin && p.x > m.x)) &&
            locallyInside(p, hole)
          ) {
            m = p;
            tanMin = tan;
          }
        }
  
        p = p.next;
      }
  
      return m;
    }
  
    // interlink polygon nodes in z-order
    function indexCurve(start, minX, minY, invSize) {
      var p = start;
      do {
        if (p.z === null) p.z = zOrder(p.x, p.y, minX, minY, invSize);
        p.prevZ = p.prev;
        p.nextZ = p.next;
        p = p.next;
      } while (p !== start);
  
      p.prevZ.nextZ = null;
      p.prevZ = null;
  
      sortLinked(p);
    }
  
    // Simon Tatham's linked list merge sort algorithm
    // http://www.chiark.greenend.org.uk/~sgtatham/algorithms/listsort.html
    function sortLinked(list) {
      var i,
        p,
        q,
        e,
        tail,
        numMerges,
        pSize,
        qSize,
        inSize = 1;
  
      do {
        p = list;
        list = null;
        tail = null;
        numMerges = 0;
  
        while (p) {
          numMerges++;
          q = p;
          pSize = 0;
          for (i = 0; i < inSize; i++) {
            pSize++;
            q = q.nextZ;
            if (!q) break;
          }
          qSize = inSize;
  
          while (pSize > 0 || (qSize > 0 && q)) {
            if (pSize !== 0 && (qSize === 0 || !q || p.z <= q.z)) {
              e = p;
              p = p.nextZ;
              pSize--;
            } else {
              e = q;
              q = q.nextZ;
              qSize--;
            }
  
            if (tail) tail.nextZ = e;
            else list = e;
  
            e.prevZ = tail;
            tail = e;
          }
  
          p = q;
        }
  
        tail.nextZ = null;
        inSize *= 2;
      } while (numMerges > 1);
  
      return list;
    }
  
    // z-order of a point given coords and inverse of the longer side of data bbox
    function zOrder(x, y, minX, minY, invSize) {
      // coords are transformed into non-negative 15-bit integer range
      x = 32767 * (x - minX) * invSize;
      y = 32767 * (y - minY) * invSize;
  
      x = (x | (x << 8)) & 0x00ff00ff;
      x = (x | (x << 4)) & 0x0f0f0f0f;
      x = (x | (x << 2)) & 0x33333333;
      x = (x | (x << 1)) & 0x55555555;
  
      y = (y | (y << 8)) & 0x00ff00ff;
      y = (y | (y << 4)) & 0x0f0f0f0f;
      y = (y | (y << 2)) & 0x33333333;
      y = (y | (y << 1)) & 0x55555555;
  
      return x | (y << 1);
    }
  
    // find the leftmost node of a polygon ring
    function getLeftmost(start) {
      var p = start,
        leftmost = start;
      do {
        if (p.x < leftmost.x) leftmost = p;
        p = p.next;
      } while (p !== start);
  
      return leftmost;
    }

    
  
    // check if a point lies within a convex triangle
    function pointInTriangle(ax, ay, bx, by, cx, cy, px, py) {
      return (
        (cx - px) * (ay - py) - (ax - px) * (cy - py) >= 0 &&
        (ax - px) * (by - py) - (bx - px) * (ay - py) >= 0 &&
        (bx - px) * (cy - py) - (cx - px) * (by - py) >= 0
      );
    }
  
    // check if a diagonal between two polygon nodes is valid (lies in polygon interior)
    function isValidDiagonal(a, b) {
      return (
        a.next.i !== b.i &&
        a.prev.i !== b.i &&
        !intersectsPolygon(a, b) &&
        locallyInside(a, b) &&
        locallyInside(b, a) &&
        middleInside(a, b)
      );
    }
  
    // signed area of a triangle
    function area(p, q, r) {
      return (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
    }
  
    // check if two points are equal
    function equals(p1, p2) {
      return p1.x === p2.x && p1.y === p2.y;
    }
  
    // check if two segments intersect
    function intersects(p1, q1, p2, q2) {
      if (
        (equals(p1, q1) && equals(p2, q2)) ||
        (equals(p1, q2) && equals(p2, q1))
      )
        return true;
      return (
        area(p1, q1, p2) > 0 !== area(p1, q1, q2) > 0 &&
        area(p2, q2, p1) > 0 !== area(p2, q2, q1) > 0
      );
    }
  
    // check if a polygon diagonal intersects any polygon segments
    function intersectsPolygon(a, b) {
      var p = a;
      do {
        if (
          p.i !== a.i &&
          p.next.i !== a.i &&
          p.i !== b.i &&
          p.next.i !== b.i &&
          intersects(p, p.next, a, b)
        )
          return true;
        p = p.next;
      } while (p !== a);
  
      return false;
    }
  
    // check if a polygon diagonal is locally inside the polygon
    function locallyInside(a, b) {
      return area(a.prev, a, a.next) < 0
        ? area(a, b, a.next) >= 0 && area(a, a.prev, b) >= 0
        : area(a, b, a.prev) < 0 || area(a, a.next, b) < 0;
    }
  
    // check if the middle point of a polygon diagonal is inside the polygon
    function middleInside(a, b) {
      var p = a,
        inside = false,
        px = (a.x + b.x) / 2,
        py = (a.y + b.y) / 2;
      do {
        if (
          p.y > py !== p.next.y > py &&
          p.next.y !== p.y &&
          px < (p.next.x - p.x) * (py - p.y) / (p.next.y - p.y) + p.x
        )
          inside = !inside;
        p = p.next;
      } while (p !== a);
  
      return inside;
    }
  
    // link two polygon vertices with a bridge; if the vertices belong to the same ring, it splits polygon into two;
    // if one belongs to the outer ring and another to a hole, it merges it into a single ring
    function splitPolygon(a, b) {
      var a2 = new Node(a.i, a.x, a.y),
        b2 = new Node(b.i, b.x, b.y),
        an = a.next,
        bp = b.prev;
  
      a.next = b;
      b.prev = a;
  
      a2.next = an;
      an.prev = a2;
  
      b2.next = a2;
      a2.prev = b2;
  
      bp.next = b2;
      b2.prev = bp;
  
      return b2;
    }
  
    // create a node and optionally link it with previous one (in a circular doubly linked list)
    function insertNode(i, x, y, last) {
      var p = new Node(i, x, y);
  
      if (!last) {
        p.prev = p;
        p.next = p;
      } else {
        p.next = last.next;
        p.prev = last;
        last.next.prev = p;
        last.next = p;
      }
      return p;
    }
  
    function removeNode(p) {
      p.next.prev = p.prev;
      p.prev.next = p.next;
  
      if (p.prevZ) p.prevZ.nextZ = p.nextZ;
      if (p.nextZ) p.nextZ.prevZ = p.prevZ;
    }
  
    function Node(i, x, y) {
      // vertice index in coordinates array
      this.i = i;
  
      // vertex coordinates
      this.x = x;
      this.y = y;
  
      // previous and next vertice nodes in a polygon ring
      this.prev = null;
      this.next = null;
  
      // z-order curve value
      this.z = null;
  
      // previous and next nodes in z-order
      this.prevZ = null;
      this.nextZ = null;
  
      // indicates whether this is a steiner point
      this.steiner = false;
    }
  
    // return a percentage difference between the polygon area and its triangulation area;
    // used to verify correctness of triangulation
    earcut.deviation = function(data, holeIndices, dim, triangles) {
      var hasHoles = holeIndices && holeIndices.length;
      var outerLen = hasHoles ? holeIndices[0] * dim : data.length;
  
      var polygonArea = Math.abs(signedArea(data, 0, outerLen, dim));
      if (hasHoles) {
        for (var i = 0, len = holeIndices.length; i < len; i++) {
          var start = holeIndices[i] * dim;
          var end = i < len - 1 ? holeIndices[i + 1] * dim : data.length;
          polygonArea -= Math.abs(signedArea(data, start, end, dim));
        }
      }
  
      var trianglesArea = 0;
      for (i = 0; i < triangles.length; i += 3) {
        var a = triangles[i] * dim;
        var b = triangles[i + 1] * dim;
        var c = triangles[i + 2] * dim;
        trianglesArea += Math.abs(
          (data[a] - data[c]) * (data[b + 1] - data[a + 1]) -
            (data[a] - data[b]) * (data[c + 1] - data[a + 1])
        );
      }
  
      return polygonArea === 0 && trianglesArea === 0
        ? 0
        : Math.abs((trianglesArea - polygonArea) / polygonArea);
    };
  
    function signedArea(data, start, end, dim) {
      var sum = 0;
      for (var i = start, j = end - dim; i < end; i += dim) {
        sum += (data[j] - data[i]) * (data[i + 1] + data[j + 1]);
        j = i;
      }
      return sum;
    }
  
    // turn a polygon in a multi-dimensional array form (e.g. as in GeoJSON) into a form Earcut accepts
    earcut.flatten = function(data) {
      var dim = data[0][0].length,
        result = {vertices: [], holes: [], dimensions: dim},
        holeIndex = 0;
  
      for (var i = 0; i < data.length; i++) {
        for (var j = 0; j < data[i].length; j++) {
          for (var d = 0; d < dim; d++) result.vertices.push(data[i][j][d]);
        }
        if (i > 0) {
          holeIndex += data[i - 1].length;
          result.holes.push(holeIndex);
        }
      }
      return result;
    };
  
    return earcut;
  }

export class Grid{
    constructor(x0, y0, x1, y1, xDivisions=10, yDivisions=10, xFunction, yFunction ){
        this.x0 = x0;
        this.y0 = y0;
        this.x1 = x1;
        this.y1 = y1;
        this.xDivisions = xDivisions;
        this.yDivisions = yDivisions;
        this.xFunction = xFunction;
        this.yFunction = yFunction;

        this.width = x1-x0;
        this.height = y1-y0;
        this.x = x0;
        this.y = y0;

        this.vertices = [];
        this.columns = [];
        /* 
        this.rows = []; */

        let xStep = this.xStep = (x1-x0) / xDivisions;
        let yStep = this.yStep = (y1-y0) / yDivisions;

        for (let c = 0; c <= xDivisions; c++) {
            this.columns[c] = [];
            let xPos = x0 + c*xStep;
            for (let r = 0; r <= yDivisions; r++) {
                let yPos = y0 + r*yStep;
                let point = { x:xPos, y:yPos };                
                this.columns[c][r] = { column:c, row:r, point:point };
                this.vertices.push( { column:c, row:r, point:point } );
            }            
        }

        // distort
        if( xFunction || yFunction ){
            console.log( "apply projection");
            this.vertices.forEach( (v)=>{
                let p = v.point;
                let projected = this.projectPoint( p );
                p.x = projected.x;
                p.y = projected.y;
            } );
        }
    }

    projectPoint( p ){
        let normalPoint = { 
            x: gn.math.map( p.x, this.x0, this.x1, 0, 1 ),
            y: gn.math.map( p.y, this.y0, this.y1, 0, 1 )
        };
        normalPoint.x = EasingTool[this.xFunction](normalPoint.x);
        normalPoint.y = EasingTool[this.yFunction](normalPoint.y);

        let projected = {
            x: gn.math.map( normalPoint.x, 0, 1, this.x0, this.x1 ),
            y: gn.math.map( normalPoint.y, 0, 1, this.y0, this.y1 )
        }
        return projected;
    }
}

/**
 * Provides utility functions for geometric operations
 * @module GeomTool
 */
export default class GeomTool{
    /*
    density: Must be 0>d>=1.  ::: 1=all pixels, 0.5= half pixels....
    */
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static lineToPoints( p0, p1, density ){
        let points = [];
        let distance = MathTool.distance( p0, p1 );
        let step = distance / (distance*density);
        //console.log( "distance: ", distance );
        //console.log( "density: ", density );
        //console.log( "step: ", step );
        for (let i = 0; i <= distance; i+=step) {
            let t = i/distance;
            let x = p5App.lerp( p0.x, p1.x, t );
            let y = p5App.lerp( p0.y, p1.y, t );
            points.push( { x:x, y:y } );
        }
        return points;
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static circleToPoints( radius, centerX=0, centerY=0, splitSize, noiseScale=0, noiseAmount=0 ){
        let perimeter = Math.PI * radius * 2;
        let numSteps = perimeter/splitSize;
        let stepAngle = (2*Math.PI) / numSteps;
        let noiseValues = GeomTool.getLoopedNoiseArray( numSteps, noiseScale, noiseAmount*-.5, noiseAmount*.5, MathTool.random()*1000000 );
        //console.log( 'numSteps, noiseScale, noiseAmount*-.5, noiseAmount*.5: ', numSteps, noiseScale, noiseAmount*-.5, noiseAmount*.5 );
        //console.log( 'noiseValues', noiseValues ); 
        let points = [];
        for (let i = 0; i < numSteps; i++) {
            let angle = i*stepAngle;
            let noiseValue = noiseValues[i];
            let x = centerX + (radius+noiseValue) * Math.cos(angle)
            let y = centerY + (radius+noiseValue) * Math.sin(angle);
            points.push( { x:x, y:y, angle:angle } );
        }
        return points;
    }

    static randomPointInRectangle( x0, y0, x1, y1 ){
        let x = MathTool.randomInRange( x1, x0 );
        let y = MathTool.randomInRange( y1, y0 );
        return { x:x, y:y };
    }

    static randomPointsInRectangle( x0, y0, x1, y1, numPoints ){
        let points = [];
        for (let i = 0; i < numPoints; i++) {
            points.push( GeomTool.randomPointInRectangle( x0, y0, x1, y1 ) );
        }
        return points;
    }

    // transforms point object {x:0,y:0} to array [0,0]
    static convertPointToArray( point ){
        return [ point.x, point.y ];
    }

    // transforms array of point objects [{x:0,y:0},{x:1,y:1} to array of arrays [ [0,0], [1,1] ]
    static convertPointsToArray( points ){
        let array = [];
        points.forEach( (p)=>{
            array.push( GeomTool.convertPointToArray(p) );
        } );
        return array;
    }

    

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static randomPointInCircle( radius, centerX=0, centerY=0 ){
        let r = radius * Math.sqrt( MathTool.random() );
        let theta = MathTool.random() * 2 * Math.PI;
        let x = centerX + r * Math.cos(theta)
        let y = centerY + r * Math.sin(theta);
        return { x:x, y:y };
    }
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static randomPointsInCircle( radius, centerX=0, centerY=0, num ){
        let points = [];
        for (let i = 0; i < num; i++) {
            let point = GeomTool.randomPointInCircle( radius, centerX, centerY );
            points.push( point );            
        }
        return points;
    }
    
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static randomPointInCircumference( radius, centerX=0, centerY=0 ){
        let angle = MathTool.random()*Math.PI*2;
        let x = centerX + radius * Math.cos(angle)
        let y = centerY + radius * Math.sin(angle);
        return { x:x, y:y };
    }
    
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static randomPointsInCircumference( radius, centerX=0, centerY=0 , num){
        let points = [];
        for (let i = 0; i < num; i++) {
            let point = GeomTool.randomPointInCircumference( radius, centerX, centerY );
            points.push( point );            
        }
        return points;
    }
    
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static randomLineInCircle( radius, length, centerX=0, centerY=0){
        let p0 = this.randomPointInCircle( radius, centerX, centerY )
        let p1;
        for (let i = 0; i < 10000; i++) {
            p1 = this.randomPointInCircumference( length, p0.x, p0.y );
            let distance2center = MathTool.distance( p1, {x:centerX, y:centerY} );
            if( distance2center <= radius ){
            break;
            }
        }
        window.p5App.line( p0.x, p0.y, p1.x, p1.y );   
    }
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static randomCurveInCircle( radius, lineLength, centerX=0, centerY=0){
        let a0 = this.randomPointInCircle( radius, centerX, centerY )
        let a1;
        for (let i = 0; i < 10000; i++) {
            a1 = this.randomPointInCircumference( lineLength, a0.x, a0.y );
            let distance2center = MathTool.distance( a1, {x:centerX, y:centerY} );
            if( distance2center <= radius ){
            break;
            }
        }
        // Controls
        let c0 = this.randomPointInCircumference( lineLength*0.5, a0.x, a0.y );
        let c1 = this.randomPointInCircumference( lineLength*0.5, a0.x, a0.y );
        window.p5App.bezier(  a0.x, a0.y, c0.x, c0.y, c1.x, c1.y, a1.x, a1.y );
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static randomPerlinCurve( x0, length, y, step, noiseOffset, amplitude){
        let result = [];
        for (let i = x0; i < x0+length; i++) {
            let r = y+p5App.noise( noiseOffset+i*step)*amplitude; 
            result.push( { x:x0+i, y:r } );         
        }
        return result;
    }


    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} colors 
     * @returns 
     */
    static randomCurve( p0, p1, controlRadius ){
      let c0 = GeomTool.randomPointInCircle( controlRadius, p0.x, p0.y );
      let c1 = GeomTool.randomPointInCircle( controlRadius, p1.x, p1.y );
      return [ c0.x, c0.y, p0.x, p0.y, p1.x, p1.y, c1.x, c1.y ];
    }

    static checkCircleCollision( p0, p1, r0, r1, maring=0 ){
        let distance = MathTool.distance( p0, p1 );
        return distance <= r0+r1+maring;
    }

    static checkCirclesCollision( circle, circleList, margin=0 ){ 
        for (let i = 0; i < circleList.length; i++) {
            let otherCircle = circleList[i];
            if( this.checkCircleCollision( circle, otherCircle, circle.r, otherCircle.r, margin ) ){
                return true;
            }
        }
        return false;
    }

    static getMinMaxDistance( point, points){
        let min = Number.MAX_VALUE;
        let max = Number.MIN_VALUE;
        for (let i = 0; i < points.length; i++) {
            let distance = MathTool.distance( point, points[i] );
            if( distance < min ){
                min = distance;
            }
            if( distance > max ){
                max = distance;
            }
        }
        return { min:min, max:max };
    }

    static createPathPolygon(points, widths) {
      points = points.map( point=> [point.x, point.y]);
      let leftPts = [];
      let rightPts = [];

        let polygon = [];
      
        for (let i = 0; i < points.length - 1; i++) {
          let [x1, y1] = points[i];
          let [x2, y2] = points[i + 1];
          let w = widths[i];
      
          let dx = x2 - x1;
          let dy = y2 - y1;
          let length = Math.sqrt(dx * dx + dy * dy);
          dx /= length;
          dy /= length;
      
          let nx = dy;
          let ny = -dx;
      
          let x3 = x2 + nx * w / 2;
          let y3 = y2 + ny * w / 2;
          let x4 = x1 + nx * w / 2;
          let y4 = y1 + ny * w / 2;
      
          if( i%2==0 ){
            leftPts.push([x4, y4]);
            rightPts.push([x3, y3]);
          }
          else{
            rightPts.push([x4, y4]);
            leftPts.push([x3, y3]);
          }
        }
      
        let [x1, y1] = points[points.length - 1];
        let w = widths[widths.length - 1];
        let dx = points[points.length - 2][0] - x1;
        let dy = points[points.length - 2][1] - y1;
        let nx = dy;
        let ny = -dx;
      
        let x3 = x1 + nx * w / 2;
        let y3 = y1 + ny * w / 2;
        let x4 = x1 - nx * w / 2;
        let y4 = y1 - ny * w / 2;
      
        rightPts.push([x3, y3]);
        leftPts.push([x4, y4]);

        polygon = [...leftPts, ...rightPts.reverse()]
        
        polygon = polygon.map( point=>{
          return { x:point[0], y:point[1] } 
        });

        return polygon;
      }
      


    static createPathPolygon_BUG(points, widths) {
      let polygon = [];
    
      for (let i = 0; i < points.length - 1; i++) {
        let [x1, y1] = [points[i].x, points[i].y];
        let [x2, y2] = [points[i + 1].x, points[i + 1].y];
        let w = widths[i];
    
        let dx = x2 - x1;
        let dy = y2 - y1;
        let length = Math.sqrt(dx * dx + dy * dy);
        dx /= length;
        dy /= length;
    
        let nx = dy;
        let ny = -dx;
    
        let x3 = x2 + nx * w / 2;
        let y3 = y2 + ny * w / 2;
        let x4 = x1 + nx * w / 2;
        let y4 = y1 + ny * w / 2;
    
        polygon.push([x4, y4]);
        polygon.push([x3, y3]);
      }
    
      let [x1, y1] = [points[points.length - 1].x, points[points.length - 1].y];
      let w = widths[widths.length - 1];
      let nx = dy;
      let ny = -dx;
    
      let x3 = x1 + nx * w / 2;
      let y3 = y1 + ny * w / 2;
      let x4 = x1 - nx * w / 2;
      let y4 = y1 - ny * w / 2;
    
      polygon.push([x3, y3]);
      polygon.push([x4, y4]);
    
      return polygon;
    }
    


    static makePointAtAngleAndDistance( angle, distance, originX=0, originY=0 ){
        let x = originX + distance * Math.cos( angle );
        let y = originY + distance * Math.sin( angle );
        return { x:x, y:y };
    }

    static getAngleBetweenThreePoints( A, B, C){
      
        var AB = Math.sqrt(Math.pow(B.x-A.x,2)+ Math.pow(B.y-A.y,2));    
        var BC = Math.sqrt(Math.pow(B.x-C.x,2)+ Math.pow(B.y-C.y,2)); 
        var AC = Math.sqrt(Math.pow(C.x-A.x,2)+ Math.pow(C.y-A.y,2));
        return Math.acos((BC*BC+AB*AB-AC*AC)/(2*BC*AB));
    }


    


    

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static createPerlinCurve(width, height, step=0.01, noiseOffset=Math.floor(MathTool.random()*10000)){
        let sineWave = [];
        for (let i = 0; i < width; i++) {
            const y = p5App.noise( noiseOffset+i*step, 0 ) * height;
            const x = i;
            sineWave.push( { x:x, y:y } );  
        }
        return sineWave;
    }

    static convertRectangleToPolygon( x, y, w, h ){
        let points = [];
        points.push( { x:x, y:y } );
        points.push( { x:x+w, y:y } );
        points.push( { x:x+w, y:y+h } );
        points.push( { x:x, y:y+h } );
        return points;
    }

    // "margin" is the ratio (lerp) between the two points, values between 0 and 0.5 (>.5 would cause zigzag, overlapping lines)
    static truncatePolygonCorners( polygon, margin ){
        let result = [];
        for (let i = 0; i < polygon.length; i++) {
            let p0 = polygon[i];
            let p1 = i==polygon.length-1 ? polygon[0] : polygon[i+1];
            let newP0 = GeomTool.getPointBetweenTwoPoints( p0, p1, margin );
            let newP1 = GeomTool.getPointBetweenTwoPoints( p0, p1, 1-margin );
            result.push( newP0 );
            result.push( newP1 );
        }
        return result;
      }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getLoopedNoiseArray( numSteps, noiseScale, minValue=0, maxValue=1, phase=0 ){
        let noiseArray = [];
        let step = p5App.TWO_PI/numSteps;
        for (let i = 0; i < numSteps; i++) {
            let angle = i*step;
            let xOffset = noiseScale*p5App.map( Math.cos((angle)+phase), -1, 1, 0, 1)
            let yOffset = noiseScale*p5App.map( Math.sin((angle)+phase), -1, 1, 0, 1)
            let value = p5App.map( p5App.noise(xOffset, yOffset), 0, 1, minValue, maxValue );
            noiseArray.push( value)
        }
        return noiseArray;
    }
            

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static createMountainCurve(width, height, invert=false, noiseFactor=0, noiseAmplitude=0){
        let curve = [];
        let noiseCurve = this.createPerlinCurve( width, 1, noiseFactor );
        for (let i = 0; i < width; i++) {
            let center = width*0.5;
            let distCenter = Math.abs(  center- i );
            let normDist = distCenter / center;
            let invNorm = 1-normDist;
            let y = (invNorm*height) + noiseCurve[i].y*noiseAmplitude-noiseAmplitude*0.5;
            if( invert )
                y = -y;
            const x = i;
            window.p5App.curve.push( { x:x, y:y } );  
        }
        return curve;
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getNearestPoints( point, others, numPoints, positionPropertyName ){
        others = [...others];
        for (let i = 0; i < others.length; i++) {
            let other = others[i];
            other.distance = 10000000000;
            other.index = i;
            if( point != other ){
                let distance = MathTool.distance( 
                    positionPropertyName?point[positionPropertyName]:point, 
                    positionPropertyName?other[positionPropertyName]:other);
                other.distance = distance;
            }
        }
        others = others.sort( (a,b)=>{
            return a.distance > b.distance ? 1 : -1;
        } );
        let nearest = others.splice( 0, numPoints );
        return nearest;
    }
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getPointsCloserThan( point, others, minDistance ){
        others = [...others];
        let closeEnough = []
        for (let i = 0; i < others.length; i++) {
            let other = others[i];
            if( point != other ){
                let distance = MathTool.distance( point, other);
                if( distance <= minDistance ){
                    closeEnough.push( other )
                }
            }
        }
        return closeEnough;
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getRandomTriangle( point, radius ){
        let p0 = GeomTool.randomPointInCircle( radius, point.x, point.y );
        let p1 = GeomTool.randomPointInCircle( radius, point.x, point.y );
        let p2 = GeomTool.randomPointInCircle( radius, point.x, point.y );
        return [p0,p1,p2];
    }

    // Adaptaed from this StackOverflow answer: https://stackoverflow.com/a/61804756/1290510
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getRandomPointsInTriangle( trianglePoints, numPoints ){
        let result = [];
        for (let i = 0; i < numPoints; i++) {
            const p = GeomTool.getRandomPointInTriangle( trianglePoints );
            result.push(p);
        }
        return result;
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static scalePoint( point, scale, originPoint ){
        let x = point.x - originPoint.x;
        let y = point.y - originPoint.y;
        x = x * scale;
        y = y * scale;
        x = x + originPoint.x;
        y = y + originPoint.y;
        return { x:x, y:y };
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static flipPoint( point, originPoint ){
        let x = point.x - originPoint.x;
        let y = point.y - originPoint.y;
        x = -x;
        y = -y;
        x = x + originPoint.x;
        y = y + originPoint.y;
        return { x:x, y:y };
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static diagonalFlipPoint( point, originPoint ){
        let x = point.x - originPoint.x;
        let y = point.y - originPoint.y;
        let temp = x;
        x = y;
        y = temp;
        x = x + originPoint.x;
        y = y + originPoint.y;
        return { x:x, y:y };
    }

    static getAngleBetweenTwoPoints( p0, p1 ){
        let angle = Math.atan2( p1.y - p0.y, p1.x - p0.x );
        return angle;
    }
    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static movePoint( point, distance, originPoint ){
        let angle = GeomTool.getPointAngle( point, originPoint );
        let xMoved = point.x + Math.cos( angle ) * distance;
        let yMoved = point.y + Math.sin( angle ) * distance;
        return { x:xMoved, y:yMoved };
    }

    static movePointAtAngle( point, distance, angle ){
        let xMoved = point.x + Math.cos( angle ) * distance;
        let yMoved = point.y + Math.sin( angle ) * distance;
        return { x:xMoved, y:yMoved };
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getTriangleArea( trianglePoints ){
        let a = MathTool.distance( trianglePoints[0], trianglePoints[1] );
        let b = MathTool.distance( trianglePoints[1], trianglePoints[2] );
        let c = MathTool.distance( trianglePoints[2], trianglePoints[0] );
        let s = (a+b+c)/2;
        let area = Math.sqrt( s*(s-a)*(s-b)*(s-c) );
        return area;
    }

    static getPolygonArea( vertices ){
        /* let area = 0;
        for (let i = 0; i < vertices.length; i++) {
            let j = (i+1) % vertices.length;
            area += vertices[i].x * vertices[j].y;
            area -= vertices[i].y * vertices[j].x;
        }
        area /= 2;
        return area; */
        let polygonTriangles = GeomTool.triangulatePolygon( vertices );
        let area = 0;
        for (let i = 0; i < polygonTriangles.length; i++) {
            let triangle = polygonTriangles[i];
            area += GeomTool.getTriangleArea( triangle );
        }
        return area;
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static scaleTriangleFromBaricenter( trianglePoints, scale ){
        let baricenter = GeomTool.getBaricenter( trianglePoints );
        let scaledTriangle = [];
        for (let i = 0; i < trianglePoints.length; i++) {
            let p = trianglePoints[i];
            let s = GeomTool.scalePoint( p, scale, baricenter );
            scaledTriangle.push( s );
        }
        return scaledTriangle;
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getBaricenter( points ){
        let x = 0;
        let y = 0;
        for (let i = 0; i < points.length; i++) {
            let p = points[i];
            x += p.x;
            y += p.y;
        }
        x /= points.length;
        y /= points.length;
        return { x:x, y:y };
    }
    

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getRandomPointsWithinTriangle( vertices, numberOfPoints ){
        let result = [];
        for (let i = 0; i < numberOfPoints; i++) {
            const p = GeomTool.getRandomPointInTriangle( vertices );
            result.push(p);
        }
        return result;
    }

    /* static getTriangleArea(triangle) {
        const [a, b, c] = triangle;
        
        return 0.5 * (
          (b.x - a.x) * (c.y - a.y) -
          (c.x - a.x) * (b.y - a.y)
        );
      } */

    // Function borrowed from 
    // https://stackoverflow.com/questions/45660743/sort-points-in-counter-clockwise-in-javascript
    // Sorts points in counter clockwise order
    static sortPointsClockWise( points ){
        // Array of points;
        //const points = [{x:?,y:?},{x:?,y:?},{x:?,y:?},...?];

        // Find min max to get center
        // Sort from top to bottom
        points.sort((a,b)=>a.y - b.y);

        // Get center y
        const cy = (points[0].y + points[points.length -1].y) / 2;

        // Sort from right to left
        points.sort((a,b)=>b.x - a.x);

        // Get center x
        const cx = (points[0].x + points[points.length -1].x) / 2;

        // Center point
        const center = {x:cx,y:cy};

        // Pre calculate the angles as it will be slow in the sort
        // As the points are sorted from right to left the first point
        // is the rightmost

        // Starting angle used to reference other angles
        var startAng;
        points.forEach(point => {
            var ang = Math.atan2(point.y - center.y,point.x - center.x);
            if(!startAng){ startAng = ang }
            else {
                if(ang < startAng){  // ensure that all points are clockwise of the start point
                    ang += Math.PI * 2;
                }
            }
            point.angle = ang; // add the angle to the point
        });


        // Sort clockwise;
        return points.sort((a,b)=> a.angle - b.angle);
    }

    static triangulatePolygon( points ){
        const earcut = createEarcut();
        const flatPoints = [];  // points.reduce((f, p) => [...f, ...p], []);
        points.forEach( p => {  
            flatPoints.push( p.x );
            flatPoints.push( p.y );
        });
        // indices to coordinate "pairs" in the "flatPoints" array
        const indices = earcut(flatPoints);
        
        const triangles = [];
        
        // three indices describe a triangle
        for (let i = 0; i < indices.length; i += 3) {
            const triangleIndices = [indices[i], indices[i + 1], indices[i + 2]];
            const points = triangleIndices.map(index => {
            const x = flatPoints[index * 2];
            const y = flatPoints[index * 2 + 1];
            
            return { x:x, y:y } ;//[x, y];
            });
            
            // A triangle is a set of 3 points: [a, b, c] where each point has the form [x, y]
            triangles.push(points);
        }
        
        return triangles;
    }

    static pointInPolygon( vertices, point ){
      let polygonTriangles = GeomTool.triangulatePolygon( vertices );
      for( let i = 0; i < polygonTriangles.length; i++ ){
        let triangle = polygonTriangles[i];
        if( GeomTool.pointInTriangle( triangle[0].x, triangle[0].y, triangle[1].x, triangle[1].y, triangle[2].x, triangle[2].y, point.x, point.y ) ){
          return true;
        }
      }
      return false;
    }

    static pointInTriangle(ax, ay, bx, by, cx, cy, px, py) {
      return (
        (cx - px) * (ay - py) - (ax - px) * (cy - py) >= 0 &&
        (ax - px) * (by - py) - (bx - px) * (ay - py) >= 0 &&
        (bx - px) * (cy - py) - (cx - px) * (by - py) >= 0
      );
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static getPointAngle( point, center ){
        let angle = Math.atan2( point.y-center.y, point.x-center.x );
        return angle;
    }

    /**
     * 
     * @param {*} p0 
     * @param {*} p1 
     * @param {*} density 
     * @returns 
     */
    static rotatePoint( point, center, angle ){
        let x = point.x - center.x;
        let y = point.y - center.y;
        let cos = Math.cos( angle );
        let sin = Math.sin( angle );
        let x1 = x*cos - y*sin;
        let y1 = x*sin + y*cos;
        x1 = x1 + center.x;
        y1 = y1 + center.y;
        return { x:x1, y:y1 };
    }

    /* static getRandomPointWithinTriangle( vertices ){
        var a = vertices[0];
        var b = vertices[1];
        var c = vertices[2];
        var r = Math.random();
        var s = Math.random();
        if(r+s>1){
            r = 1-r;
            s = 1-s;
        }
        var x = a.x*r + b.x*s + c.x*(1-r-s);
        var y = a.y*r + b.y*s + c.y*(1-r-s);
        return new Point(x,y);
    } */

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static getRandomPointInTriangle( points ){
        let p0 = points[0];
        let p1 = points[1];
        let p2 = points[2];

        let r1 = MathTool.random();
        let r2 = MathTool.random();

        let sqrtR1 = Math.sqrt(r1);

        let x = (1 - sqrtR1) * p0.x + (sqrtR1 * (1 - r2)) * p1.x + (sqrtR1 * r2) * p2.x;
        let y = (1 - sqrtR1) * p0.y + (sqrtR1 * (1 - r2)) * p1.y + (sqrtR1 * r2) * p2.y;

        return {x:x, y:y}
    }

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static getPointBetweenTwoPoints( p0, p1, lerp=.5 ){
        let distX = p1.x - p0.x;
        let distY = p1.y - p0.y;
        let midX = p0.x + distX*lerp;
        let midY = p0.y + distY*lerp;
        return {x:midX, y:midY}
    }

    /**
     * 
     * @param {*} p0
     * @param {*} p0
     * @param {*} step Distance in pixels between each point
     * @returns 
     */
    static getPointsBetweenTwoPoints( p0, p1, step ){
        let distX = p1.x - p0.x;
        let distY = p1.y - p0.y;
        let dist = Math.sqrt( distX*distX + distY*distY );
        let numPoints = Math.floor( dist / step );
        let points = [];
        for (let i = 0; i <= numPoints; i++) {
            let lerp = i/numPoints;
            let midX = p0.x + distX*lerp;
            let midY = p0.y + distY*lerp;
            points.push( {x:midX, y:midY} );
        }
        return points;        
    }

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static samplePoints(nPoints, W, H){
        let points = [];
        for (let i = 0; i < nPoints; i++) {
          let x = Math.floor(MathTool.random()*W );
          let y = Math.floor(MathTool.random()*H );
          let clr = p5App.get( x, y );
          if( clr[0] < 150 ){
            points.push( {x:x, y:y} );
          }
        }
        //console.log( "sampled: ", points.length);    
        return points;
    }



    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static nosifyPoints(pts, amount){
        let results = [];
        pts.forEach(p => {
            let newP = GeomTool.randomPointInCircle( amount, p.x, p.y );
            results.push( newP );
        });
        return results;
    }

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static getNodeGarden(points){
        let result = {
            points: [], 
            lines: [],
            faces: []
        };

        for (let i = 0; i < points.length; i++) {
            const point = points[i];
            let closest = GeomTool.getPointsCloserThan(point, points, 100).splice(0,3);
            let face = closest;
            result.faces.push( face );
        }
        return result;
    }
    

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static generateGridObject( x0, y0, x1, y1, xDivisions=10, yDivisions=10, xFunction, yFunction ){
        let gridObject = new Grid( x0, y0, x1, y1, xDivisions, yDivisions, xFunction, yFunction );
        return gridObject;
    }
    
    /* Generates a list of points positioned on a grid with the requested attributes */
    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static generateGrid( x0, y0, x1, y1, xDivisions=10, yDivisions=10, xTicks, yTicks ){
        xTicks = xTicks || xDivisions;
        yTicks = yTicks || yDivisions;
        let xStep = (x1-x0) / xDivisions;
        let yStep = (y1-y0) / yDivisions;
        /* let xTicks = (x1-x0) * xDensity;
        let yTicks = (y1-y0) * yDensity; */
        let xTickStep = (x1-x0) / xTicks;
        let yTickStep = (y1-y0) / yTicks;

        let result = [];
        for (let r = 0; r <= yDivisions; r++) {
            let yPos = y0 + r*yStep;
            for (let c = 0; c <= xTicks; c++) {
                let xPos = x0 + c*xTickStep;
                let point = { x:xPos, y:yPos };                
                result.push( point );
            }            
        }
        for (let c = 0; c <= xDivisions; c++) {
            let xPos = x0 + c*xStep;
            for (let r = 0; r <= yTicks; r++) {
                let yPos = y0 + r*yTickStep;
                let point = { x:xPos, y:yPos };                
                result.push( point );
            }            
        }
        /* for (let r = 0; r <= yDivisions; r++) {
            let yPos = y0 + r*yStep;
            for (let c = 0; c <= xDivisions; c++) {
                let xPos = x0 + c*xStep;
                let point = { x:xPos, y:yPos };                
                result.push( point );
            }            
        } */
        return result;
    }

    static movePointsToCentroid(points, distance) {
      const centroid = GeomTool.getCentroid(points);
      return points.map(point => GeomTool.movePointTowards(point, centroid, distance));
    }
    
    static getCentroid(points) {
      let x = 0;
      let y = 0;
      for (const point of points) {
        x += point.x;
        y += point.y;
      }
      x /= points.length;
      y /= points.length;
      return {x, y};
    }
    
    static movePointTowards(point, target, distance) {
      const currentDistance = MathTool.distance(point, target);
      if (currentDistance <= distance || currentDistance === 0) {
        return target;
      }
      const angle = Math.atan2(target.y - point.y, target.x - point.x);
      return {
        x: point.x + Math.cos(angle) * distance,
        y: point.y + Math.sin(angle) * distance
      };
    }


    static snapPointsToGrid( array, gridSize ){
      let results = [];
      for (let i = 0; i < array.length; i++) {
        const p = array[i];
        const pOut = structuredClone(p);
        pOut.x = Math.round(pOut.x/gridSize)*gridSize;
        pOut.y = Math.round(pOut.y/gridSize)*gridSize;
        results.push( pOut );
      }
      return results;
    }

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static getPolygon(x, y, radius, npoints, startAngle=0) {
        let angle = p5App.TWO_PI / npoints;
        let pts = [];
        for (let a = 0; a < p5App.TWO_PI; a += angle) {
          let sx = x + Math.cos(a+startAngle) * radius;
          let sy = y + Math.sin(a+startAngle) * radius;
          pts.push( {x:sx, y:sy} );
        }
        return pts;
      }

    /*
    Function take from here: https://github.com/gdenisov/cardinal-spline-js
    params:
        points
        tension
        resolution
        closed
    */
    /**
     * 
     * @param {*} points 
     * @returns 
     */
        static getCurvePoints(points,tension,resolution,closed){

        points = GeomTool.pointsArrayToFlatArray(points);
        //var splinePoints = DrawUtils.getCurvePoints( ptsFlat );
        
        if(typeof points==="undefined"||points.length<2){return new Float32Array(0)}tension=typeof tension==="number"?tension:0.5;resolution=typeof resolution==="number"?resolution:25;var j,d=1,e=points.length,n=0,m=(e-2)*resolution+2+(closed?2*resolution:0),k=new Float32Array(m),a=new Float32Array((resolution+2)<<2),b=4;j=points.slice(0);if(closed){j.unshift(points[e-1]);j.unshift(points[e-2]);j.push(points[0],points[1])}else{j.unshift(points[1]);j.unshift(points[0]);j.push(points[e-2],points[e-1])}a[0]=1;for(;d<resolution;d++){var o=d/resolution,p=o*o,r=p*o,q=r*2,s=p*3;a[b++]=q-s+1;a[b++]=s-q;a[b++]=r-2*p+o;a[b++]=r-p}a[++b]=1;g(j,a,e,tension);if(closed){j=[];j.push(points[e-4],points[e-3],points[e-2],points[e-1],points[0],points[1],points[2],points[3]);g(j,a,4,tension)}function g(G,z,B,M){for(var A=2,H;A<B;A+=2){var C=G[A],D=G[A+1],E=G[A+2],F=G[A+3],I=(E-G[A-2])*M,J=(F-G[A-1])*M,K=(G[A+4]-C)*M,L=(G[A+5]-D)*M,u=0,v,w,x,y;for(H=0;H<resolution;H++){v=z[u++];w=z[u++];x=z[u++];y=z[u++];k[n++]=v*C+w*E+x*I+y*K;k[n++]=v*D+w*F+x*J+y*L}}}e=closed?0:points.length-2;k[n++]=points[e++];k[n]=points[e];
        
        k = GeomTool.flatArrayToPointsArray( k );
        return k;
    };

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static pointsArrayToFlatArray(pts){
      let ptsFlat = [];
      pts.forEach( (pt)=>{
        ptsFlat.push( pt.x, pt.y );
      });
      //console.log( ptsFlat);
      return ptsFlat;
    }

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static flatArrayToPointsArray(ptsFlat){
      let pts = [];
      for (let i = 0; i < ptsFlat.length; i+=2) {
        pts.push( {x:ptsFlat[i], y:ptsFlat[i+1]} );        
      }
      //console.log( pts);
      return pts;
    }

    /*
    Functions borrowed from this stackoverflow thread: 
    https://stackoverflow.comline/questions/37224912/circle-line-segment-collision/37225895#37225895
    */
    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static intersectCircleLine(circle, line){
        var a, b, c, d, u1, u2, ret, retP1, retP2, v1, v2;
        v1 = {};
        v2 = {};
        v1.x = line.p2.x - line.p1.x;
        v1.y = line.p2.y - line.p1.y;
        v2.x = line.p1.x - circle.center.x;
        v2.y = line.p1.y - circle.center.y;
        b = (v1.x * v2.x + v1.y * v2.y);
        c = 2 * (v1.x * v1.x + v1.y * v1.y);
        b *= -2;
        d = Math.sqrt(b * b - 2 * c * (v2.x * v2.x + v2.y * v2.y - circle.radius * circle.radius));
        if(isNaN(d)){ // no intercept
            return [];
        }
        u1 = (b - d) / c;  // these represent the unit distance of point one and two on the line
        u2 = (b + d) / c;    
        retP1 = {};   // return points
        retP2 = {}  
        ret = []; // return array
        if(u1 <= 1 && u1 >= 0){  // add point if on the line segment
            retP1.x = line.p1.x + v1.x * u1;
            retP1.y = line.p1.y + v1.y * u1;
            ret[0] = retP1;
        }
        if(u2 <= 1 && u2 >= 0){  // second add point if on the line segment
            retP2.x = line.p1.x + v1.x * u2;
            retP2.y = line.p1.y + v1.y * u2;
            ret[ret.length] = retP2;
        }       
        return ret;
    }

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static intersectLines(line,line1){  
        var v1, v2, v3, c, u;
        v1 = {};
        v2 = {};
        v3 = {};
        v1.x = line.p1.x - line.p0.x; // vector of line
        v1.y = line.p1.y - line.p0.y;
        v2.x = line1.p1.x - line1.p0.x; //vector of line2
        v2.y = line1.p1.y - line1.p0.y;
        var c = v1.x * v2.y - v1.y * v2.x; // cross of the two vectors
        if(c !== 0){ 
            v3.x = line.p0.x - line1.p0.x; 
            v3.y = line.p0.y - line1.p0.y;                
            u = (v2.x * v3.y - v2.y * v3.x) / c; // unit distance of intercept point on this line
            return {x : line.p0.x + v1.x * u, y : line.p0.y + v1.y * u};
        }
        return undefined;
    }

    static intersection(segment1, segment2) {
      const x1 = segment1.p0.x, y1 = segment1.p0.y, x2 = segment1.p1.x, y2 = segment1.p1.y;
      const x3 = segment2.p0.x, y3 = segment2.p0.y, x4 = segment2.p1.x, y4 = segment2.p1.y;
    
      const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
      if (denominator === 0) {
        return null;
      }
    
      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
    
      if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
        const x = x1 + t * (x2 - x1);
        const y = y1 + t * (y2 - y1);
        return {x, y};
      }
    
      return null;
    }

    static lineIntersectionsWithRect(line, topLeft, topRight, bottomLeft, bottomRight) {
      const intersections = [];
      const top = {p0: topLeft, p1: topRight};
      const right = {p0: topRight, p1: bottomRight};
      const bottom = {p0: bottomLeft, p1: bottomRight};
      const left = {p0: topLeft, p1: bottomLeft};
      const sides = [top, right, bottom, left];
      sides.forEach(side => {
        const intersection = this.intersection(line, side);
        if (intersection) {
          intersections.push(intersection);
        }
      });
      return intersections;
    }

    

    /**
 * Liang-Barsky function by Daniel White 
 * 
 * @link http://www.skytopia.com/project/articles/compsci/clipping.html
 *
 * @param  {number}        x0
 * @param  {number}        y0
 * @param  {number}        x1
 * @param  {number}        y1
 * @param  {array<number>} bbox
 * @return {array<array<number>>|null}
 */
static liangBarskyLineRectIntersect (x0, y0, x1, y1, bbox) {
  var [xmin, xmax, ymin, ymax] = bbox;
  var t0 = 0, t1 = 1;
  var dx = x1 - x0, dy = y1 - y0;
  var p, q, r;

  for(var edge = 0; edge < 4; edge++) {   // Traverse through left, right, bottom, top edges.
    if (edge === 0) { p = -dx; q = -(xmin - x0); }
    if (edge === 1) { p =  dx; q =  (xmax - x0); }
    if (edge === 2) { p = -dy; q = -(ymin - y0); }
    if (edge === 3) { p =  dy; q =  (ymax - y0); }

    r = q / p;

    if (p === 0 && q < 0) return null;   // Don't draw line at all. (parallel line outside)

    if(p < 0) {
      if (r > t1) return null;     // Don't draw line at all.
      else if (r > t0) t0 = r;     // Line is clipped!
    } else if (p > 0) {
      if(r < t0) return null;      // Don't draw line at all.
      else if (r < t1) t1 = r;     // Line is clipped!
    }
  }

  return { 
    p0: { x: x0 + t0 * dx, y: y0 + t0 * dy },
    p1: { x: x0 + t1 * dx, y: y0 + t1 * dy }
  }
    /* [x0 + t0 * dx, y0 + t0 * dy],
    [x0 + t1 * dx, y0 + t1 * dy]
  ]; */
}
    

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static liftLine(line,dist){
        var v1,l
        v1 = {};
        v1.x = line.p2.x - line.p1.x; // convert line to vector
        v1.y = line.p2.y - line.p1.y;
        l = Math.sqrt(v1.x * v1.x + v1.y * v1.y); // get length;
        v1.x /= l;  // Assuming you never pass zero length lines
        v1.y /= l;
        v1.x *= dist; // set the length
        v1.y *= dist;
        // move the line along its normal the required distance
        line.p1.x -= v1.y;
        line.p1.y += v1.x;
        line.p2.x -= v1.y;
        line.p2.y += v1.x;
    
        return line; // if needed 
    }

    /**
     * 
     * @param {*} points 
     * @returns 
     */
    static circleDistFromLineSeg(circle,line){
        var v1, v2, v3, u;
        v1 = {};
        v2 = {};
        v3 = {};
        v1.x = line.p2.x - line.p1.x;
        v1.y = line.p2.y - line.p1.y;
        v2.x = circle.center.x - line.p1.x;
        v2.y = circle.center.y - line.p1.y;
        u = (v2.x * v1.x + v2.y * v1.y) / (v1.y * v1.y + v1.x * v1.x); // unit dist of point on line
        if(u >= 0 && u <= 1){
            v3.x = (v1.x * u + line.p1.x) - circle.center.x;
            v3.y = (v1.y * u + line.p1.y) - circle.center.y;
            v3.x *= v3.x;
            v3.y *= v3.y;
            return Math.sqrt(v3.y + v3.x); // return distance from line
        } 
        // get distance from end points
        v3.x = circle.center.x - line.p2.x;
        v3.y = circle.center.y - line.p2.y;
        v3.x *= v3.x;  // square vectors
        v3.y *= v3.y;    
        v2.x *= v2.x;
        v2.y *= v2.y;
        return Math.min(Math.sqrt(v2.y + v2.x), Math.sqrt(v3.y + v3.x)); // return smaller of two distances as the result
    }


}